import type { ComputedRef, Ref } from "vue";
import useListFilter from "~/composables/use-list/use-list-filter";

export interface ITagFilter {
  id: string;
  type: "tagType" | "teamColor-playerNumber" | "tagTeam";
  metadata: {
    tagType: IModelTag["type"];
    tagPlayerNumber: IModelTag["playerNumber"];
    tagTeam: IModelIdentityItemTag | undefined;
  };
  filterFn: (comparedTag: IModelTag) => boolean;
}

interface useTagFiltersOptions {
  searchValue?: Ref<string> | ComputedRef<string>;
}

export default function useTagFilters(tags: Ref<IModelTag[]>, options?: useTagFiltersOptions) {
  const activeFilters = ref(new Map<ITagFilter["id"], ITagFilter>());
  const filters = computed(() => {
    const filters = new Map<ITagFilter["id"], ITagFilter>();
    tags.value.forEach((tag) => {
      const typeFilter = {
        id: `tag.type=${tag.type}`,
        type: "tagType" as const,
        metadata: {
          tagType: tag.type,
          tagPlayerNumber: tag.playerNumber,
          tagTeam: tag.tagTeam,
        },
        filterFn: (comparedTag: IModelTag) => comparedTag.type === tag.type,
      };
      filters.set(typeFilter.id, typeFilter);

      if (tag.tagTeam && tag.tagTeam.title) {
        const teamFilters = {
          id: `tag.team=${tag.tagTeam?.title}`,
          type: "tagTeam" as const,
          metadata: {
            tagType: tag.type,
            tagPlayerNumber: tag.playerNumber,
            tagTeam: tag.tagTeam,

          },
          strict: true,
          filterFn: (comparedTag: IModelTag) => comparedTag.tagTeam?.title === tag.tagTeam?.title,
        };
        filters.set(teamFilters.id, teamFilters);
      }

      // Since filter combined of multiple ones we're checking for existence of both fields
      if (tag.playerNumber && tag.tagTeam?.color) {
        const playerFilter = {
          id: `tag.teamColor=${tag.tagTeam?.color};tag.playerNumber${tag.playerNumber}`,
          type: "teamColor-playerNumber" as const,
          metadata: {
            tagType: tag.type,
            tagPlayerNumber: tag.playerNumber,
            tagTeam: tag.tagTeam,
          },
          filterFn: (comparedTag: IModelTag) => comparedTag.tagTeam?.color === tag.tagTeam?.color && comparedTag.playerNumber === tag.playerNumber,
        };
        filters.set(playerFilter.id, playerFilter);
      }
    });
    // TODO: Simplify
    return Object.values(Object.fromEntries(filters.entries()));
  });

  const data = computed(() => {
    const searchedTags = useListFilter(tags, { searchProperty: "title", searchValue: options?.searchValue });
    const sortedByTimeTags = searchedTags.value.sort((a, b) => a.startTime - b.startTime);
    if (!activeFilters || activeFilters.value.size === 0)
      return sortedByTimeTags;

    const typeFilter = [...activeFilters.value].filter(([_filterId, filter]) => filter.type === "tagType");
    const teamFilters = [...activeFilters.value].filter(([_filterId, filter]) => filter.type === "tagTeam");
    const playerFilter = [...activeFilters.value].filter(([_filterId, filter]) => filter.type === "teamColor-playerNumber");

    return sortedByTimeTags
      .filter(tag => typeFilter.length ? typeFilter.some(([_filterId, filter]) => filter.filterFn(tag)) : true)
      .filter(tag => teamFilters.length ? teamFilters.some(([_filterId, filter]) => filter.filterFn(tag)) : true)
      .filter(tag => playerFilter.length ? playerFilter.some(([_filterId, filter]) => filter.filterFn(tag)) : true);
  });
  return { filters, activeFilters, data };
}
